import React, { useEffect, useState } from 'react';
import { XIcon } from './icons';

// Module singleton latch: index.tsx registers the service worker before React
// mounts, so onNeedRefresh can fire before the toast exists. Holding the
// callback here means a mount that arrives late still picks it up.
let pending: (() => void) | null = null;
let notify: ((reload: () => void) => void) | null = null;

/** Called by index.tsx when a new build is waiting in the service worker. */
export function announceUpdateReady(reload: () => void) {
    pending = reload;
    notify?.(reload);
}

export const UpdateToast: React.FC = () => {
    // `() => pending`, not `pending`: a bare function initial value is treated
    // as a lazy initializer and CALLED — which would reload the page instantly.
    const [reload, setReload] = useState<(() => void) | null>(() => pending);

    useEffect(() => {
        notify = (fn) => setReload(() => fn);
        return () => { notify = null; };
    }, []);

    if (!reload) return null;

    return (
        <div
            role="status"
            className="fixed left-1/2 -translate-x-1/2 z-[9999] bottom-[calc(1rem+env(safe-area-inset-bottom))]
                       flex items-center gap-3 px-4 py-3 max-w-[calc(100vw-2rem)]
                       bg-app-surface border border-app-border rounded-card shadow-lg animate-fade-in-down"
        >
            <p className="text-sm text-text-ink font-medium">A new version of Dekko is ready.</p>
            <button
                onClick={reload}
                className="shrink-0 px-3 py-1.5 rounded-btn bg-action hover:bg-action-dark text-white text-sm font-semibold transition-colors"
            >
                Reload
            </button>
            <button
                onClick={() => { pending = null; setReload(null); }}
                aria-label="Dismiss update notice"
                className="shrink-0 text-text-faint hover:text-text-muted p-1 rounded-full transition-colors"
            >
                <XIcon className="w-4 h-4" />
            </button>
        </div>
    );
};
